import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import AddressForm from "@/components/AddressForm";
import { ArrowLeft, Plus, House, Briefcase, MapPin, PencilSimple, Trash, CheckCircle } from "@phosphor-icons/react";

export default function MyAddresses() {
  const { user, loading } = useAuth();
  const { selectedAddress, setSelectedAddress } = useCart();
  const nav = useNavigate();
  const [addrs, setAddrs] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/customer/addresses");
      setAddrs(r.data);
      // Keep cart's selectedAddress in sync with server truth
      if (selectedAddress) {
        const updated = r.data.find((a) => a.id === selectedAddress.id);
        if (!updated) {
          // The selected address was deleted — promote to the new default if any
          const def = r.data.find((a) => a.is_default) || r.data[0] || null;
          setSelectedAddress(def);
        } else if (JSON.stringify(updated) !== JSON.stringify(selectedAddress)) {
          setSelectedAddress(updated);
        }
      }
    } catch {
      setAddrs([]);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    load();
    // eslint-disable-next-line
  }, [user, loading, nav]);

  const remove = async (a) => {
    if (!confirm(`Delete "${a.label}" address?`)) return;
    try {
      await api.delete(`/customer/addresses/${a.id}`);
      toast.success("Address removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const setDefault = async (a) => {
    try {
      await api.post(`/customer/addresses/${a.id}/default`);
      toast.success("Default address updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] pb-16">
      <header className="sticky top-0 z-40 glass border-b border-gray-100 px-4 py-3 flex items-center gap-2">
        <Link to="/home" className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-testid="addresses-back">
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1">
          <div className="font-heading font-black">My addresses</div>
          <div className="text-xs text-gray-500">Manage saved delivery addresses</div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-3">
        <button onClick={() => { setEditing(null); setShowForm(true); }}
          data-testid="addresses-add-new-page"
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-dashed border-[#15803D] text-[#15803D] hover:bg-[#15803D]/5 transition font-bold">
          <Plus size={18} weight="bold" />
          Add new address
        </button>

        {addrs === null && <div className="text-center py-8 text-gray-400">Loading…</div>}
        {addrs?.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <MapPin size={36} className="mx-auto mb-2 text-gray-300" />
            No saved addresses yet
          </div>
        )}

        {addrs?.map((a) => {
          const Icon = a.label === "Work" ? Briefcase : a.label === "Other" ? MapPin : House;
          return (
            <div key={a.id} className="qd-card p-4" data-testid={`addr-page-${a.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F8CB46]/30 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} weight="fill" className="text-[#15803D]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading font-black">{a.label}</span>
                    {a.is_default && (
                      <span className="text-[9px] uppercase tracking-widest font-bold bg-[#15803D] text-white px-1.5 py-0.5 rounded-full">Default</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700">{a.receiver_name} · {a.receiver_mobile}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {a.address}{a.landmark ? `, ${a.landmark}` : ""}{a.city ? `, ${a.city}` : ""} · <span className="font-mono">{a.pincode}</span>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex gap-2 flex-wrap">
                {!a.is_default && (
                  <button onClick={() => setDefault(a)}
                    className="text-xs font-bold px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center gap-1"
                    data-testid={`addr-page-default-${a.id}`}>
                    <CheckCircle size={14} /> Set default
                  </button>
                )}
                <button onClick={() => { setEditing(a); setShowForm(true); }}
                  className="text-xs font-bold px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center gap-1"
                  data-testid={`addr-page-edit-${a.id}`}>
                  <PencilSimple size={14} /> Edit
                </button>
                <button onClick={() => remove(a)}
                  className="text-xs font-bold px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1"
                  data-testid={`addr-page-delete-${a.id}`}>
                  <Trash size={14} /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <AddressForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
